import { FlightCategory } from './flight-category.model';
import { SkyCondition } from './sky-condition.model';
import { Wind } from './wind.model';
import { TurbulenceModel } from './turbulence.model';
import { IcingModel } from './icing.model';


/**
 * Change indicator of a forecast block
 */
export type ChangeIndicator = 'FM'|'TEMPO'|'BECMG'|'PROB';


/**
 * Model for an individual forecast period within a TAF
 */
export class Forecast {
  /**
   * Change indicator
   */
  changeIndicator: ChangeIndicator;

  /**
   * Start time when the forecast block is valid
   */
  validFrom: Date;

  /**
   * End time when the forecast block is valid
   */
  validTo: Date;

  /**
   * Percent probability (if changeIndicator is "PROB"), returned as a formatted string.
   */
  probability: string;

  /**
   * Flight category
   */
  flightCategory: FlightCategory;

  /**
   * Ceiling (formatted as a string)
   */
  ceiling: string;

  /**
   * Visibility (in statute miles), returned as a formatted HTML string (possibly with alert/warn CSS class)
   */
  visibility: string;

  /**
   * Weather string (raw text)
   */
  wxString: string;

  /**
   * Weather string (translated), returned as a formatted HTML string (possible with alert/warn CSS classes)
   */
  wxStringTrans: string;

  /**
   * Wind
   */
  wind: Wind;

  /**
   * Wind shear
   */
  windShearHgtFtAgl?: string;
  windShearSpeedKt?: string;
  windShearDirDegrees?: string;

  turbulence: Array<TurbulenceModel>;

  icing: Array<TurbulenceModel>;

  /**
   * Sky condition array
   */
  skyConditions: Array<SkyCondition>;

  /**
   * Parses a TAF forecast period from the API
   * @param data
   * @returns {Forecast}
   */
  static parse(data: any): Forecast {
    return {
      changeIndicator: data.change_indicator || null,
      validFrom: data.fcst_time_from ? new Date(data.fcst_time_from) : null,
      validTo: data.fcst_time_to ? new Date(data.fcst_time_to) : null,
      probability: data.probability || null,
      flightCategory: data.flight_category || null,
      ceiling: data.ceiling || null,
      visibility: (data.visibility_statute_mi && data.visibility_statute_mi !== 'Unknown') ? data.visibility_statute_mi : null,
      wxString: data.wx_string || null,
      wxStringTrans: data.wx_string_trans || null,
      wind: Wind.parse(data.wind_dir_degrees, data.wind_speed_kt, data.wind_gust_kt),
      windShearHgtFtAgl: data.wind_shear_hgt_ft_agl,
      windShearSpeedKt: data.wind_shear_speed_kt,
      windShearDirDegrees: data.wind_shear_dir_degrees,
      skyConditions: (data.sky_conditions_trans || []).map(skc => SkyCondition.parse(skc.cloud_base, skc.sky_cover)),
      turbulence: (data.turbulence_conditions || []).map(turb => TurbulenceModel.parse(turb.min_alt, turb.max_alt, turb.intensity)),
      icing: (data.icing_conditions || []).map(ice => IcingModel.parse(ice.min_alt, ice.max_alt, ice.intensity))
    };
  }
}
